import { Anchor, Badge, Button, Code, Group, Modal, Table, Text, Textarea } from '@mantine/core';
import { Fragment, useCallback, useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';

const formatTimestamp = (value) => (value ? new Date(value).toLocaleString() : '');

const EnforcementBadge = ({ enforced }) => (
  <Badge color={enforced ? 'red' : 'gray'} variant="light">
    {enforced ? 'enforced' : 'alert-only'}
  </Badge>
);

const ReleaseHoldModal = ({ domainId, hold, onClose, onReleased }) => {
  const fetch = useFetch();
  const [releaseNote, setReleaseNote] = useState('');
  const [error, setError] = useState();
  const [submitting, setSubmitting] = useState(false);

  const submit = (event) => {
    event.preventDefault();
    setError(undefined);
    setSubmitting(true);
    fetch(`/api/admin/domains/${domainId}/emailHolds/${hold.id}/release`, {
      method: 'POST',
      body: JSON.stringify({ releaseNote: releaseNote.trim() }),
    })
      .then(() => {
        onReleased();
        onClose();
      })
      .catch((e) => {
        setError(e.message);
        setSubmitting(false);
      });
  };

  return (
    <Modal opened onClose={onClose} title={`Release the ${hold.reason} hold`}>
      <form onSubmit={submit}>
        <Textarea
          label="Release note"
          withAsterisk
          description="Recorded on the hold, so the next admin can see why sending was resumed."
          rows={4}
          value={releaseNote}
          onChange={(event) => setReleaseNote(event.target.value)}
        />
        {error && <ErrorMessage message={error} />}
        <Group justify="flex-end" gap="xs" pt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="red" loading={submitting} disabled={releaseNote.trim() === ''}>
            Release
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

// Every hold episode this domain has had, enforced or not: the flags are off during
// the observation week, so the panel is the only way to see what enforcement would
// have stopped. Any episode without `releasedAt` can be released; `domainBlock` usually
// clears itself, and `blocklist` is derived from the HetrixTools verdict rather than stored.
const EmailHoldsAdminPanel = ({ domainId, onReleased }) => {
  const fetch = useFetch();
  const [data, setData] = useState();
  const [loadError, setLoadError] = useState();
  const [releasing, setReleasing] = useState();
  const [expandedEvidenceId, setExpandedEvidenceId] = useState();

  const load = useCallback(() => {
    setLoadError(undefined);
    fetch(`/api/admin/domains/${domainId}/emailHolds`).then(setData).catch(setLoadError);
  }, [domainId, fetch]);

  useEffect(load, [load]);

  if (data === undefined) {
    return loadError ? <ErrorMessage message={loadError.message} /> : <Spinner />;
  }

  return (
    <>
      {/* A reload after a release keeps showing the last good data instead of replacing it. */}
      {loadError && <ErrorMessage message={loadError.message} />}
      {/* Mounted only while releasing, so each open starts with an empty note. */}
      {releasing && (
        <ReleaseHoldModal
          domainId={domainId}
          hold={releasing}
          onClose={() => setReleasing(undefined)}
          onReleased={() => {
            load();
            onReleased();
          }}
        />
      )}
      {/* The admin deploy is independent of the API one that adds `blocklist`. */}
      {data.blocklist && (
        <>
          <Group gap="xs" mb={4}>
            <Text size="sm" fw={500}>
              Blocklist verdicts
            </Text>
            <EnforcementBadge enforced={data.blocklist.enforced} />
          </Group>
          <Text size="xs" c="dimmed" mb="xs">
            Derived from the latest HetrixTools verdict rather than stored, so it cannot be released - it clears
            automatically when the HetrixTools verdict is clean.
          </Text>
          <Table.ScrollContainer minWidth={700} mb="md">
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Host</Table.Th>
                  <Table.Th>Listed on</Table.Th>
                  <Table.Th>Listed since</Table.Th>
                  <Table.Th>Last checked</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.blocklist.hosts.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text size="sm" c="dimmed" ta="center" py="md">
                        No verdict recorded for this domain&apos;s sending hosts yet.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {data.blocklist.hosts.map((host) => (
                  <Table.Tr key={host.host}>
                    <Table.Td>{host.host}</Table.Td>
                    <Table.Td>
                      {host.listedOn.map((listing) => (
                        <Anchor
                          key={listing.rbl}
                          href={listing.delist}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                          display="block"
                        >
                          {listing.rbl}
                        </Anchor>
                      ))}
                    </Table.Td>
                    <Table.Td>{formatTimestamp(host.listedSince)}</Table.Td>
                    <Table.Td>{formatTimestamp(host.lastCheckedAt)}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Badge color={host.fresh ? 'green' : 'gray'} variant="light">
                          {host.fresh ? 'fresh' : 'stale'}
                        </Badge>
                        <Badge color={host.actionable ? 'red' : 'gray'} variant="light">
                          {host.actionable ? 'actionable' : 'not actionable'}
                        </Badge>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}
      <Text size="sm" fw={500} mb="xs">
        Hold episodes
      </Text>
      <Table.ScrollContainer minWidth={900}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Held at</Table.Th>
              <Table.Th>Released at</Table.Th>
              <Table.Th>Released by</Table.Th>
              <Table.Th>Release note</Table.Th>
              <Table.Th>Enforcement</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.holds.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    This domain has never been held.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {data.holds.map((hold) => (
              <Fragment key={hold.id}>
                <Table.Tr>
                  <Table.Td>{hold.reason}</Table.Td>
                  <Table.Td>{formatTimestamp(hold.heldAt)}</Table.Td>
                  <Table.Td>{hold.releasedAt ? formatTimestamp(hold.releasedAt) : 'Active'}</Table.Td>
                  <Table.Td>{hold.releasedBy}</Table.Td>
                  <Table.Td>{hold.releaseNote}</Table.Td>
                  <Table.Td>
                    <EnforcementBadge enforced={hold.enforced} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      {hold.evidence && (
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          onClick={() => setExpandedEvidenceId(expandedEvidenceId === hold.id ? undefined : hold.id)}
                        >
                          Evidence
                        </Button>
                      )}
                      {!hold.releasedAt && (
                        <Button size="compact-xs" color="red" onClick={() => setReleasing(hold)}>
                          Release
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
                {expandedEvidenceId === hold.id && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Code block>{JSON.stringify(hold.evidence, null, 2)}</Code>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
};

export default EmailHoldsAdminPanel;
